import { Component, type ErrorInfo, type ReactNode, type CSSProperties } from "react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠️</div>
            <h2 style={styles.title}>
              {this.props.fallbackTitle || "Something went wrong loading this studio"}
            </h2>
            <p style={styles.desc}>
              An unexpected error occurred while rendering the page component.
            </p>
            {this.state.error && (
              <pre style={styles.errorBox}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            )}
            <div style={styles.actions}>
              <button
                type="button"
                style={styles.retryBtn}
                onClick={this.handleReset}
              >
                🔄 Try Again
              </button>
              <button
                type="button"
                style={styles.reloadBtn}
                onClick={() => window.location.reload()}
              >
                🔃 Reload Page
              </button>
              <button
                type="button"
                style={styles.homeBtn}
                onClick={() => (window.location.href = "/admin/dashboard")}
              >
                📊 Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "32px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60vh",
  },
  card: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "28px 24px",
    maxWidth: 640,
    width: "100%",
    textAlign: "center",
  },
  icon: {
    fontSize: 36,
    marginBottom: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 8px 0",
  },
  desc: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: "0 0 16px 0",
  },
  errorBox: {
    background: "rgba(224, 80, 64, 0.08)",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    borderRadius: 6,
    padding: "12px",
    color: "#ff8b7b",
    fontSize: 12,
    textAlign: "left",
    overflowX: "auto",
    maxHeight: 200,
    marginBottom: 20,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  retryBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  reloadBtn: {
    background: "var(--surface, #18181b)",
    color: "var(--text, #f4f4f5)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
  },
  homeBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
  },
};
